import { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  ChevronDownIcon, 
  XIcon, 
  SearchIcon,
  EyeIcon 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  type Payment, 
  type Invoice, 
  type Customer, 
  type Subscription, 
  customerServices, 
  subscriptionServices 
} from '../lib/supabase';
import { fixedPaymentServices, fixedInvoiceServices } from '../lib/fixed-invoice-payment-services';
import { formatCurrency, formatInvoiceNumber } from '../lib/formatters';

interface EnrichedPayment extends Payment {
  invoice?: Invoice;
  customerName?: string;
}

const Payments = () => {
  const [payments, setPayments] = useState<EnrichedPayment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentPayment, setCurrentPayment] = useState<Partial<Payment> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch payments and related data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError('');
      
      try {
        // Get payments using fixed service
        const { data: paymentsData, error: paymentsError } = await fixedPaymentServices.getAll();
        if (paymentsError) throw new Error('Failed to fetch payments');
        
        // Get invoices using fixed service
        const { data: invoicesData, error: invoicesError } = await fixedInvoiceServices.getAll();
        if (invoicesError) throw new Error('Failed to fetch invoices');
        
        // Get subscriptions
        const { data: subscriptionsData, error: subscriptionsError } = await subscriptionServices.getAll();
        if (subscriptionsError) throw new Error('Failed to fetch subscriptions');
        
        // Get customers
        const { data: customersData, error: customersError } = await customerServices.getAll();
        if (customersError) throw new Error('Failed to fetch customers');
        
        // Set all fetched data
        setInvoices(invoicesData || []);
        setSubscriptions(subscriptionsData || []);
        setCustomers(customersData || []);
        
        // Enrich payments with invoice and customer data
        const enrichedPayments = (paymentsData || []).map(payment => {
          const invoice = invoicesData?.find(inv => inv.id === payment.invoice_id);
          const subscription = invoice ? subscriptionsData?.find(s => s.id === invoice.subscription_id) : undefined;
          const customer = subscription ? customersData?.find(c => c.id === subscription.customer_id) : undefined;
          
          return {
            ...payment,
            invoice,
            customerName: customer?.name || 'Unknown'
          };
        });
        
        setPayments(enrichedPayments);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle opening the modal for create/edit
  const handleOpenModal = (payment?: EnrichedPayment) => {
    if (payment) {
      setCurrentPayment(payment);
    } else {
      setCurrentPayment({
        invoice_id: '',
        payment_method: 'card',
        amount: 0,
        status: 'pending',
        transaction_id: '',
        paid_at: new Date().toISOString()
      });
    }
    setShowModal(true);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentPayment(null);
    setError('');
  };

  // Handle form submission for create/edit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!currentPayment) {
      setError('Payment data is missing');
      return;
    }

    // Validate required fields
    if (!currentPayment.invoice_id) {
      setError('Please select an invoice');
      return;
    }
    if (!currentPayment.payment_method) {
      setError('Please select a payment method');
      return;
    }
    if (!currentPayment.amount || currentPayment.amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!currentPayment.paid_at) {
      setError('Please enter a payment date');
      return;
    }

    try {
      let savedPayment: Payment | undefined;

      if (currentPayment.id) {
        // Update existing payment using fixed service
        const { data, error: updateError } = await fixedPaymentServices.update(
          currentPayment.id,
          currentPayment as Payment
        );
        if (updateError) throw updateError;
        savedPayment = data?.[0];

        // Update payments state
        if (savedPayment) {
          setPayments(prevPayments =>
            prevPayments.map(payment =>
              payment.id === currentPayment.id
                ? { ...payment, ...savedPayment }
                : payment
            )
          );
        }
      } else {
        // Create new payment using fixed service
        const newPayment: Payment = {
          ...currentPayment as Payment,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        const { data, error: createError } = await fixedPaymentServices.create(newPayment);
        if (createError) throw createError;
        savedPayment = data?.[0];

        // Update payments state and related invoice
        if (savedPayment) {
          const invoice = invoices.find(inv => inv.id === savedPayment?.invoice_id);
          const subscription = invoice ? subscriptions.find(s => s.id === invoice.subscription_id) : undefined;
          const customer = subscription ? customers.find(c => c.id === subscription.customer_id) : undefined;

          setPayments(prevPayments => [
            ...prevPayments,
            { ...savedPayment!, customerName: customer?.name || 'Unknown' }
          ]);

          // Update invoice paid status if payment is successful
          if (savedPayment.status === 'success' && invoice && !invoice.paid_at && invoice.id) {
            const updatedInvoice = {
              ...invoice,
              paid_at: savedPayment.paid_at,
              status: 'paid'
            };
            
            await fixedInvoiceServices.update(invoice.id, updatedInvoice);
            setInvoices(prevInvoices =>
              prevInvoices.map(inv =>
                inv.id === invoice.id ? updatedInvoice : inv
              )
            );
          }
        }
      }

      handleCloseModal();
    } catch (err) {
      console.error('Error saving payment:', err);
      setError(err instanceof Error ? err.message : 'Failed to save payment');
    }
  };

  // Handle payment deletion
  const handleDelete = async (paymentId: string) => {
    if (!paymentId || !window.confirm('Are you sure you want to delete this payment?')) return;
    
    try {
      const { error: deleteError } = await fixedPaymentServices.delete(paymentId);
      if (deleteError) throw deleteError;
      
      setPayments(prevPayments => 
        prevPayments.filter(p => p.id !== paymentId)
      );
    } catch (err) {
      console.error('Error deleting payment:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete payment');
    }
  };

  // Filter payments based on search query
  const filteredPayments = payments.filter(payment =>
    payment.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payment.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payment.payment_method.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payment.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (payment.transaction_id && payment.transaction_id.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Helper function to get invoice details
  const getInvoiceDetails = (invoiceId: string) => {
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (!invoice) return { amount: 0, customerName: 'Unknown' };

    const subscription = subscriptions.find(s => s.id === invoice.subscription_id);
    if (!subscription) return { amount: invoice.amount, customerName: 'Unknown' };

    const customer = customers.find(c => c.id === subscription.customer_id);
    return {
      amount: invoice.amount,
      customerName: customer?.name || 'Unknown'
    };
  };

  // Helper function to get status color
  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <PlusIcon size={16} className="mr-1" /> Record Payment
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-800">
            <XIcon size={16} />
          </button>
        </div>
      )}

      <div className="mb-4 relative">
        <input
          type="text"
          placeholder="Search payments..."
          className="px-10 py-2 border rounded w-full"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="absolute left-3 top-3 text-gray-400">
          <SearchIcon size={18} />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-10">Loading payments data...</div>
      ) : filteredPayments.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          {searchQuery ? 'No payments match your search.' : 'No payments found.'}
        </div>
      ) : (
        <div className="overflow-x-auto shadow rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paid Date
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.customerName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <Link to={`/invoices/${payment.invoice_id}`} className="text-blue-600 hover:text-blue-800">
                      {payment.invoice ? formatInvoiceNumber(payment.invoice) : 'N/A'}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(payment.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {payment.payment_method === 'card' ? 'Credit Card' : 
                     payment.payment_method === 'bank_transfer' ? 'Bank Transfer' : 
                     payment.payment_method}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(payment.paid_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                    <Link
                      to={`/payments/${payment.id}`}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Payment Details"
                    >
                      <EyeIcon size={16} />
                    </Link>
                    <button
                      onClick={() => handleOpenModal(payment)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Edit Payment"
                    >
                      <PencilIcon size={16} />
                    </button>
                    <button
                      onClick={() => payment.id && handleDelete(payment.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Payment"
                    >
                      <TrashIcon size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
            <button 
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <XIcon size={20} />
            </button>

            <h2 className="text-xl font-semibold mb-4">
              {currentPayment?.id ? 'Edit Payment' : 'Record New Payment'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice*
                </label>
                <div className="relative">
                  <select
                    value={currentPayment?.invoice_id || ''}
                    onChange={(e) => {
                      const selectedInvoice = invoices.find(inv => inv.id === e.target.value);
                      setCurrentPayment({
                        ...currentPayment,
                        invoice_id: e.target.value,
                        amount: selectedInvoice?.amount || 0
                      });
                    }}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    required
                  >
                    <option value="">Select an invoice</option>
                    {invoices
                      .filter(invoice => invoice.status !== 'paid')
                      .map(invoice => {
                        const { customerName } = getInvoiceDetails(invoice.id!);
                        return (
                          <option key={invoice.id} value={invoice.id}>
                            {customerName} - Invoice #{formatInvoiceNumber(invoice)} - {formatCurrency(invoice.amount)} (Due: {formatDate(invoice.due_date)})
                          </option>
                        );
                      })
                    }
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <ChevronDownIcon size={16} />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method*
                </label>
                <select
                  value={currentPayment?.payment_method || 'card'}
                  onChange={(e) => setCurrentPayment({
                    ...currentPayment,
                    payment_method: e.target.value
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="card">Credit Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="paypal">PayPal</option>
                  <option value="crypto">Cryptocurrency</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount*
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={currentPayment?.amount || ''}
                  onChange={(e) => setCurrentPayment({
                    ...currentPayment,
                    amount: parseFloat(e.target.value)
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={currentPayment?.status || 'success'}
                  onChange={(e) => setCurrentPayment({
                    ...currentPayment,
                    status: e.target.value
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="success">Success</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transaction ID
                </label>
                <input
                  type="text"
                  value={currentPayment?.transaction_id || ''}
                  onChange={(e) => setCurrentPayment({
                    ...currentPayment,
                    transaction_id: e.target.value
                  })}
                  placeholder="Optional"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Date*
                </label>
                <input
                  type="date"
                  value={currentPayment?.paid_at ? currentPayment.paid_at.toString().substring(0, 10) : ''}
                  onChange={(e) => setCurrentPayment({
                    ...currentPayment,
                    paid_at: e.target.value
                  })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                />
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {currentPayment?.id ? 'Update' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
